import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, euro, API } from "@/lib/api";
import { CheckCircle2, Download, Loader2, Clock, Landmark, XCircle, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";

const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderNumber = params.get("order");
  const [order, setOrder] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const [attempts, setAttempts] = useState(0);
  const [bank, setBank] = useState(null);

  const loadOrder = useCallback(async () => {
    if (!orderNumber) return null;
    const { data } = await api.get(`/orders/${orderNumber}`);
    setOrder(data);
    return data;
  }, [orderNumber]);

  useEffect(() => {
    api.get("/config").then((r) => setBank(r.data.bank_details)).catch(() => {});
  }, []);

  useEffect(() => {
    let stop = false;
    const run = async () => {
      if (!sessionId) { await loadOrder(); return; }
      if (attempts > MAX_POLL_ATTEMPTS) { setPolling(false); await loadOrder(); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPolling(false);
          await loadOrder();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setPolling(false); await loadOrder(); return;
        }
      } catch (err) {
        console.error("Payment status poll failed:", err);
      }
      if (!stop) setTimeout(() => setAttempts((a) => a + 1), POLL_INTERVAL_MS);
    };
    run();
    return () => { stop = true; };
  }, [sessionId, attempts, loadOrder]);

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  if (polling) {
    return (
      <div className="max-w-xl mx-auto px-4 py-32 text-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto" />
        <h1 className="font-display text-2xl font-bold text-white mt-6">Confirming your payment…</h1>
        <p className="text-slate-400 mt-2">Please wait, we are verifying with the payment provider.</p>
      </div>
    );
  }

  if (!order) {
    return <div className="max-w-xl mx-auto px-4 py-32 text-center text-slate-400" data-testid="order-loading">Loading order…</div>;
  }

  const paid = order.payment_status === "paid";
  const pending = order.payment_status === "pending";
  const failed = order.payment_status === "failed";
  const downloadUrl = order.download_token ? `${API}/download/${order.order_number}/${order.download_token}` : null;

  let statusHeading = "Payment Failed";
  let statusColor = "text-rose-400";
  if (paid) { statusHeading = "Payment Successful"; statusColor = "text-emerald-400"; }
  else if (pending) { statusHeading = "Payment Pending"; statusColor = "text-amber-400"; }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <div className="tactical-card p-8 sm:p-10 text-center" data-testid="payment-result">
        {paid && <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />}
        {pending && <Clock className="w-16 h-16 text-amber-400 mx-auto" />}
        {failed && <XCircle className="w-16 h-16 text-rose-400 mx-auto" />}

        <h1 className="font-display text-3xl font-extrabold text-white mt-6" data-testid="payment-status-heading">
          {statusHeading}
        </h1>
        <p className="text-slate-400 mt-2">
          {paid && `Thank you, ${order.customer_name}.`}
          {pending && order.payment_method === "bank_transfer" && "Complete your bank transfer using the details below. Your download unlocks once we confirm it."}
          {pending && order.payment_method === "paypal" && "Your order is recorded. PayPal confirmation is being finalised — your download will be enabled once payment is confirmed."}
          {failed && "Your payment was not completed. Please try again."}
        </p>

        <div className="bg-[#0A0E1A] border border-[#1E2F4D] rounded-xl p-5 mt-8 text-left space-y-3 text-sm">
          <Row l="Order number" v={<span className="font-mono text-cyan-400">{order.order_number}</span>} />
          <Row l="Product" v={order.product_title} />
          <Row l="Amount" v={euro(order.amount)} />
          <Row l="Payment method" v={order.payment_method.replace("_", " ")} />
          <Row l="Status" v={<span className={statusColor}>{order.status}</span>} />
        </div>

        {paid && order.license_key && (
          <div className="bg-[#0A0E1A] border border-[#2563EB] rounded-xl p-5 mt-6 text-left" data-testid="license-key-block">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2"><KeyRound className="w-3.5 h-3.5 text-cyan-400" /> Your license key</div>
            <button onClick={() => copy(order.license_key)} className="w-full text-left font-mono text-white text-lg tracking-wide flex items-center justify-between hover:text-cyan-400" data-testid="license-key-value">
              {order.license_key} <Copy className="w-4 h-4 shrink-0" />
            </button>
          </div>
        )}

        {paid && downloadUrl && (
          <a href={downloadUrl} data-testid="download-file-btn"
            className="w-full mt-8 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white font-semibold px-6 py-4 rounded-full text-lg">
            <Download className="w-6 h-6" /> Download Your Product
          </a>
        )}
        {paid && downloadUrl && order.download_token_expires_at && (
          <p className="mt-3 text-xs text-slate-500" data-testid="download-expiry">
            {typeof order.downloads_remaining === "number" && (
              <span data-testid="downloads-remaining">{order.downloads_remaining} of {order.download_limit} downloads remaining. </span>
            )}
            This secure link expires on {new Date(order.download_token_expires_at).toLocaleDateString()}. Your receipt email contains the same link.
          </p>
        )}
        {paid && !downloadUrl && (
          <p className="mt-8 text-sm text-amber-400" data-testid="download-not-ready">
            Payment confirmed. The product file has not been uploaded by the administrator yet — you'll receive an email as soon as it's available.
          </p>
        )}

        {pending && order.payment_method === "bank_transfer" && bank && (
          <div className="mt-8 text-left" data-testid="bank-details">
            <h3 className="font-display font-semibold text-white flex items-center gap-2 mb-3"><Landmark className="w-5 h-5 text-cyan-400" /> Bank transfer details</h3>
            <div className="bg-[#0A0E1A] border border-[#1E2F4D] rounded-xl p-5 space-y-3 text-sm font-mono">
              <CopyRow l="Bank" v={bank.bank_name} onCopy={copy} />
              <CopyRow l="Holder" v={bank.account_holder} onCopy={copy} />
              <CopyRow l="IBAN" v={bank.iban} onCopy={copy} />
              <CopyRow l="BIC" v={bank.bic} onCopy={copy} />
              <CopyRow l="Reference" v={order.order_number} onCopy={copy} />
              <CopyRow l="Amount" v={euro(order.amount)} onCopy={copy} />
            </div>
          </div>
        )}

        {failed && (
          <Link to="/products" className="w-full mt-8 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white font-semibold px-6 py-3.5 rounded-full">
            Back to Catalog
          </Link>
        )}

        <p className="text-xs text-slate-500 mt-6">A receipt has been sent to your email. No account or login is required.</p>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return <div className="flex items-center justify-between gap-4"><span className="text-slate-500">{l}</span><span className="text-slate-200 text-right">{v}</span></div>;
}
function CopyRow({ l, v, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{l}</span>
      <button onClick={() => onCopy(v)} className="text-slate-200 flex items-center gap-2 hover:text-cyan-400">{v} <Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}
